import { ErrorOutlineOutlined as ErrorOutlineIcon } from "@mui/icons-material";
import { Divider } from "@mui/material";
import { groupBy } from "lodash";
import { FC, useEffect, useMemo } from "react";
import { CSVLink } from "react-csv";
import { useTranslation } from "react-i18next";
import { useRecoilValue } from "recoil";

import { PIC } from "@ryde/assets";
import {
  GroupTitleTemplate,
  PreviewDrawerLineStyled,
  RydeProgress,
} from "@ryde/controls";
import { useTranslatedLists } from "@ryde/redux";
import {
  BOOKING_BULK_CREATE_PANEL_LABEL_KEYS,
  bookingBulkCreateStateFromPollingState,
  BookingConflictErrorType,
  BookingPrepareConflictErrorType,
  bulkApprovalBookingConflictsUpdatedAtom,
  DECIMAL_RADIX,
  IBookingPrepareConflict,
  IBulkBookingsPanelLabelKeys,
  isBulkBookingSetFinishSelector,
  isBulkBookingValidationFinishSelector,
  Log,
  resolveRydeProgressColor,
  SINGLE_KEY,
  skipPreviewStepsIfNoErrorsSelector,
  translateInfoItem,
} from "@ryde/shared";
import { blueMui, rydeColors } from "@ryde/theme";

import { DrawerFormWrapperStyled } from "../../drawer";
import { BulkPreviewStepConflictsContext } from "./bulk-preview-step-conflicts.context";
import { BulkPreviewStepUploadConflictsContext } from "./bulk-preview-step-upload-conflicts.context";
import {
  PreviewStepAdornment,
  ResolveRejectedBookingTable,
  ResolveRejectedBookingUploadTable,
} from "./components";
import { progressTitleResolve } from "./helpers";
import { useBulkPreviewStep } from "./hooks";

interface IPreviewStepFormProps {
  bookingCount: number;
  successfullyBooked: number;
  uploadConflicts?: IBookingPrepareConflict[];
  onSaveClick?: () => void;
  onRefresh: () => Promise<void>;
  onAbort: () => Promise<void>;
  /** The wording of this panel; defaults to the create panel's. */
  labelKeys?: IBulkBookingsPanelLabelKeys;
  /** Passed straight to the conflicts context - see {@link IBulkPreviewStepConflictsContext}. */
  isGapConflictApprovable?: boolean;
}

export const PreviewStepForm: FC<IPreviewStepFormProps> = ({
  bookingCount,
  successfullyBooked,
  uploadConflicts,
  onRefresh,
  onAbort,
  onSaveClick,
  labelKeys = BOOKING_BULK_CREATE_PANEL_LABEL_KEYS,
  isGapConflictApprovable,
}) => {
  const { t } = useTranslation();
  const { bookingConflictsCSVHeadersTranslated } = useTranslatedLists();
  const bulkApprovalBookingConflictsUpdated = useRecoilValue(
    bulkApprovalBookingConflictsUpdatedAtom,
  );

  const {
    isLoading,
    loadPercentage,
    isPreview,
    errors,
    conflictErrors,
    translatableErrors,
    current,
    totalCount,
  } = useRecoilValue(bookingBulkCreateStateFromPollingState);
  const isBulkBookingSetFinish = useRecoilValue(isBulkBookingSetFinishSelector);
  const isBulkBookingValidationFinish = useRecoilValue(
    isBulkBookingValidationFinishSelector,
  );
  const skipPreviewStepsIfNoErrors = useRecoilValue(
    skipPreviewStepsIfNoErrorsSelector,
  );

  const { resolveApprovalBookingConflicts } = useBulkPreviewStep();

  const approvalBookingConflictsArray = useMemo(
    () => [...Array.from(bulkApprovalBookingConflictsUpdated.values()).flat()],
    [bulkApprovalBookingConflictsUpdated],
  );

  const groupedConflicts = useMemo(
    () => groupBy(approvalBookingConflictsArray, "errorType"),
    [approvalBookingConflictsArray],
  );
  const uploadConflictsGrouped = useMemo(
    () => groupBy(uploadConflicts || [], "errorType"),
    [uploadConflicts],
  );

  const hasConflicts = useMemo(() => {
    return Object.keys(groupedConflicts).length > 0;
  }, [groupedConflicts]);
  const hasUploadConflicts = useMemo(() => {
    return Object.keys(uploadConflictsGrouped).length > 0;
  }, [uploadConflictsGrouped]);

  const progressTitle = progressTitleResolve({
    isLoading,
    isBulkBookingSetFinish,
    isBulkBookingValidationFinish,
    isPreview,
    errors,
    conflictErrors,
    current,
    totalCount,
    labelKeys,
    t,
  });
  const progressColor = resolveRydeProgressColor({
    isFinished: isBulkBookingSetFinish,
    error: errors?.at(-1),
    hasConflictError: Boolean(conflictErrors?.length),
  });

  const conflictsData = useMemo(
    () =>
      approvalBookingConflictsArray.map(conflictData => {
        return {
          ...conflictData,
          errorType: t(
            `booking.formLabel.conflictsDetails.${conflictData.errorType}`,
          ),
          approvalReason: conflictData.approvalReason?.name ?? "",
        };
      }),
    [approvalBookingConflictsArray, t],
  );

  useEffect(() => {
    if (!conflictErrors?.length) {
      return;
    }

    for (const conflictError of conflictErrors) {
      resolveApprovalBookingConflicts({
        conflictError,
      });
    }
  }, [conflictErrors, resolveApprovalBookingConflicts]);

  const isProcessFinished = useMemo(
    () => isBulkBookingValidationFinish || isBulkBookingSetFinish,
    [isBulkBookingValidationFinish, isBulkBookingSetFinish],
  );

  const shouldAutoSave = useMemo(() => {
    const shouldAutoSetBooking =
      !conflictErrors?.length &&
      isProcessFinished &&
      skipPreviewStepsIfNoErrors &&
      !hasConflicts;
    Log.debug(
      "Should auto-save:",
      shouldAutoSetBooking,
      "skipPreviewStepsIfNoErrors",
      skipPreviewStepsIfNoErrors,
      "is process finished",
      isProcessFinished,
      "conflict errors:",
      conflictErrors?.length,
    );
    return shouldAutoSetBooking;
  }, [
    isProcessFinished,
    conflictErrors?.length,
    skipPreviewStepsIfNoErrors,
    hasConflicts,
  ]);

  useEffect(() => {
    if (shouldAutoSave) {
      onSaveClick?.();
    }
  }, [shouldAutoSave, onSaveClick]);

  return (
    <DrawerFormWrapperStyled>
      <RydeProgress
        value={parseInt(String(loadPercentage || 0), DECIMAL_RADIX)}
        title={progressTitle}
        color={progressColor}
        isLoading={isLoading}
        EndAdornment={
          <PreviewStepAdornment
            isLoading={isLoading}
            isPreview={isPreview}
            onRefresh={onRefresh}
            onAbort={onAbort}
          />
        }
      />
      {isPreview && !!translatableErrors?.length && (
        <ul className="errors-wrapper">
          {translatableErrors?.map(error => (
            <li key={`${error.translationKey}`} className="error-item">
              <ErrorOutlineIcon
                fontSize="small"
                color="error"
                className="error-icon"
              />
              <span className="error-text">{translateInfoItem(error, t)}</span>
            </li>
          ))}
        </ul>
      )}
      {isPreview && (
        <GroupTitleTemplate name="preview" formLabel={t("general.PREVIEW")} />
      )}
      <PreviewDrawerLineStyled>
        <span>{t("booking.bulk.totalBookings")}</span>
        <span>{bookingCount}</span>
      </PreviewDrawerLineStyled>
      <PreviewDrawerLineStyled $numberColor={rydeColors.green}>
        <span>
          {t(
            isBulkBookingSetFinish
              ? labelKeys.resultFinished
              : labelKeys.resultPending,
          )}
        </span>
        <span>{successfullyBooked}</span>
      </PreviewDrawerLineStyled>

      {hasUploadConflicts && <Divider sx={{ my: "10px" }} />}
      {(
        Object.keys(uploadConflictsGrouped) as BookingPrepareConflictErrorType[]
      ).map(errorType => {
        return (
          <BulkPreviewStepUploadConflictsContext.Provider
            key={errorType}
            value={{
              conflicts: uploadConflictsGrouped[errorType],
            }}
          >
            <ResolveRejectedBookingUploadTable
              errorType={errorType}
              key={errorType}
            />
          </BulkPreviewStepUploadConflictsContext.Provider>
        );
      })}

      {hasConflicts && <Divider sx={{ my: "10px" }} />}
      {(Object.keys(groupedConflicts) as BookingConflictErrorType[]).map(
        errorType => {
          return (
            <BulkPreviewStepConflictsContext.Provider
              key={errorType}
              value={{
                conflicts: groupedConflicts[errorType],
                onRefresh,
                isPreview,
                isGapConflictApprovable,
                titleTranslationKeys: labelKeys.conflictTitles,
              }}
            >
              <ResolveRejectedBookingTable
                errorType={errorType}
                key={errorType}
              />
            </BulkPreviewStepConflictsContext.Provider>
          );
        },
      )}
      <Divider sx={{ my: "10px" }} />
      <CSVLink
        data={conflictsData}
        headers={bookingConflictsCSVHeadersTranslated}
        filename={`${t(`booking.${SINGLE_KEY}`)}-${t(`booking.formLabel.conflicts`)}.${Number(new Date())}.csv`}
        style={{
          color: blueMui._600,
          display: "flex",
          flexDirection: "row",
          gap: "6px",
        }}
      >
        <PIC.DownloadSVG />
        <span>{t("booking.bulk.downloadCSV")}</span>
      </CSVLink>
    </DrawerFormWrapperStyled>
  );
};
